/**
 * Dataset List Page
 *
 * Displays all datasets with their stats.
 */

import {
  Button,
  Card,
  CardBody,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Database } from 'lucide-react';

import { useDatasets } from '@/lib/api/hooks/datasets';
import { formatRelativeTime } from '@/lib/design-system';

export const Route = createFileRoute('/datasets/')({
  component: DatasetsPage,
});

function DatasetsPage() {
  const navigate = useNavigate();
  const { data: datasets, isLoading, error } = useDatasets();

  return (
    <div className="container-page py-4 animate-fade-in">
      <div className="mb-4 flex items-center justify-end">
        <Button
          as={Link}
          to="/datasets/new"
          color="primary"
          size="sm"
        >
          Create Dataset
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      )}

      {error && (
        <Card className="border border-default-100 shadow-none">
          <CardBody className="text-center text-xs text-danger">
            Failed to load datasets. Please try again.
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && datasets && datasets.length === 0 && (
        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="rounded-full bg-default-100 p-3">
              <Database className="h-5 w-5 text-default-500" />
            </div>
            <p className="text-xs text-default-500">
              No datasets yet
            </p>
            <Button
              as={Link}
              to="/datasets/new"
              color="primary"
              size="sm"
            >
              Create Dataset
            </Button>
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && datasets && datasets.length > 0 && (
        <Table
          aria-label="Datasets table"
          selectionMode="none"
          classNames={{
            wrapper: 'shadow-none border border-default-100',
            th: 'text-xs font-medium text-default-500 bg-default-50',
            td: 'text-xs py-2',
          }}
        >
          <TableHeader>
            <TableColumn>NAME</TableColumn>
            <TableColumn>FILES</TableColumn>
            <TableColumn>ANNOTATIONS</TableColumn>
            <TableColumn>CREATED</TableColumn>
          </TableHeader>
          <TableBody>
            {datasets.map((dataset) => (
              <TableRow
                key={dataset.id}
                className="cursor-pointer transition-colors hover:bg-default-50"
                onClick={() => navigate({ to: `/datasets/${dataset.id}` })}
              >
                <TableCell>
                  <div>
                    <p className="text-xs font-medium">{dataset.name}</p>
                    {dataset.description && (
                      <p className="text-xs text-default-500 truncate max-w-xs">
                        {dataset.description}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat">
                    {dataset.audio_count ?? 0} files
                  </Chip>
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat" color="secondary">
                    {dataset.annotation_set_count ?? 0} versions
                  </Chip>
                </TableCell>
                <TableCell className="text-default-500">
                  {formatRelativeTime(new Date(dataset.created_at))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
